import type * as mapboxgl from 'mapbox-gl';

export type Issue =
  | { id: string; featureIds?: string[]; kind: 'kinks'; message: string; center: mapboxgl.LngLatLike }
  | { id: string; featureIds?: string[]; kind: 'orientation'; message: string; bbox: mapboxgl.LngLatBoundsLike }
  | { id: string; featureIds?: string[]; kind: 'hole'; message: string; bbox: mapboxgl.LngLatBoundsLike }
  | { id: string; kind: 'overlap'; message: string; ids: [string, string]; bbox: mapboxgl.LngLatBoundsLike };

const PROGRESS_FILL = 'rgba(25, 118, 210, 0.18)';

export type WhispActivity = 'idle' | 'pending' | 'queued' | 'running';

export type TracesActivity = 'idle' | 'waiting' | 'computing' | 'failed' | 'held';

const TRACES_ACTIVITY_BACKGROUND: Record<TracesActivity, string> = {
  idle: '',
  waiting: '#fff8e1',
  computing: `repeating-linear-gradient(45deg, ${PROGRESS_FILL} 0 8px, transparent 8px 16px)`,
  failed: '#f5f5f5',
  held: '#fff8e1',
};

export class ErrorListControl implements mapboxgl.IControl {
  private _map: mapboxgl.Map | null = null;
  private _container!: HTMLElement;
  private list!: HTMLElement;
  private errs: Issue[] = [];
  private selectedId: string | null = null;
  private status!: HTMLElement;
  private tracesRow!: HTMLElement;
  private tracesState: TracesActivity = 'idle';
  private whispRow!: HTMLElement;
  private whispState: WhispActivity = 'idle';
  private busy = false;
  private fixAllButton!: HTMLButtonElement;
  private notice = '';
  private cancelButton!: HTMLButtonElement;
  private recheckButton!: HTMLButtonElement;

  setProgress(phase: string, fraction: number) {
    if (!this.status) return;
    const percent = Math.round(Math.max(0, Math.min(1, fraction)) * 100);
    this.status.textContent = phase;
    this.status.hidden = false;
    this.status.setAttribute('role', 'progressbar');
    this.status.setAttribute('aria-valuemin', '0');
    this.status.setAttribute('aria-valuemax', '100');
    this.status.setAttribute('aria-valuenow', String(percent));
    this.status.style.background = `linear-gradient(to right, ${PROGRESS_FILL} ${percent}%, transparent ${percent}%)`;
  }

  setTracesActivity(state: TracesActivity, text: string) {
    this.tracesState = state;
    if (this.tracesRow) {
      this.tracesRow.textContent = text;
      this.tracesRow.hidden = state === 'idle';
      this.tracesRow.setAttribute('data-state', state);
      this.tracesRow.style.background = TRACES_ACTIVITY_BACKGROUND[state];
    }
    this.render();
  }

  setWhispActivity(state: WhispActivity, text: string) {
    this.whispState = state;
    if (this.whispRow) {
      this.whispRow.textContent = text;
      this.whispRow.hidden = state === 'idle';
      this.whispRow.setAttribute('data-state', state);
    }
    this.render();
  }

  private clearProgress() {
    this.status.setAttribute('role', 'status');
    for (const attribute of ['aria-valuemin', 'aria-valuemax', 'aria-valuenow']) this.status.removeAttribute(attribute);
    this.status.style.background = '';
  }

  setNotice(message: string) {
    this.notice = message;
    this.render();
  }

  setBusy(busy: boolean, message = 'Checking polygon issues…') {
    this.busy = busy;
    if (this.status) {
      this.clearProgress();
      this.status.textContent = busy ? message : '';
      this.status.hidden = !busy;
    }
    this.render();
  }

  removeIssuesFor(ids: string[]) {
    const removed = new Set(ids);
    this.setErrors(this.errs.filter((e) => e.kind !== 'overlap' || !e.ids.some((id) => removed.has(id))));
  }

  constructor(
    private onRowClick: (it: Issue) => void,
    private onFixOverlap: (issue: Extract<Issue, { kind: 'overlap' }>) => void,
    private onFixAll: () => void,
    private onCancel: () => void,
    private translate: (key: string) => string,
    private onRecheck: () => void
  ) {}

  onAdd(map: mapboxgl.Map) {
    this._map = map;
    const root = document.createElement('div');
    root.className = 'mapboxgl-ctrl mapboxgl-ctrl-group validation-ctrl';
    root.style.display = 'none';
    root.style.maxWidth = '360px';
    root.style.minWidth = '220px';
    root.style.flexDirection = 'column';

    const header = document.createElement('div');
    header.textContent = this.translate('Map_filter_issues_tr');
    header.style.fontSize = '12px';
    header.style.fontWeight = '600';
    header.style.padding = '6px 8px';
    header.style.borderBottom = '1px solid #eee';
    header.style.display = 'flex';
    header.style.alignItems = 'center';
    header.style.justifyContent = 'space-between';
    header.style.gap = '12px';
    this.fixAllButton = document.createElement('button');
    this.fixAllButton.type = 'button';
    this.fixAllButton.textContent = 'Fix all';
    this.fixAllButton.style.cssText =
      'width:auto;height:auto;padding:4px 8px;font-size:12px;line-height:1.4;text-transform:uppercase;';
    this.fixAllButton.addEventListener('click', () => this.onFixAll());
    header.appendChild(this.fixAllButton);
    this.cancelButton = document.createElement('button');
    this.cancelButton.type = 'button';
    this.cancelButton.textContent = this.translate('cancel_tr');
    this.cancelButton.style.cssText =
      'display:none;width:auto;height:auto;padding:4px 8px;font-size:12px;text-transform:uppercase;';
    this.cancelButton.hidden = true;
    this.cancelButton.addEventListener('click', () => this.onCancel());
    header.appendChild(this.cancelButton);
    this.recheckButton = document.createElement('button');
    this.recheckButton.type = 'button';
    this.recheckButton.textContent = this.translate('Map_check_again_tr');
    this.recheckButton.style.cssText = this.cancelButton.style.cssText;
    this.recheckButton.hidden = true;
    this.recheckButton.addEventListener('click', () => this.onRecheck());
    header.appendChild(this.recheckButton);

    const wrap = document.createElement('div');
    wrap.style.maxHeight = '280px';
    wrap.style.overflow = 'auto';

    const list = document.createElement('div');
    list.style.display = 'flex';
    list.style.flexDirection = 'column';
    list.style.gap = '6px';
    list.style.padding = '6px';

    wrap.appendChild(list);
    root.appendChild(header);
    this.status = document.createElement('div');
    this.status.setAttribute('role', 'status');
    this.status.setAttribute('data-testid', 'map-issues-status');
    this.status.style.padding = '8px';
    this.status.style.fontSize = '12px';
    this.status.hidden = true;
    root.appendChild(this.status);
    this.tracesRow = document.createElement('div');
    this.tracesRow.setAttribute('role', 'status');
    this.tracesRow.setAttribute('data-testid', 'map-traces-activity');
    this.tracesRow.style.padding = '8px';
    this.tracesRow.style.fontSize = '12px';
    this.tracesRow.style.borderTop = '1px solid #eee';
    this.tracesRow.hidden = true;
    root.appendChild(this.tracesRow);
    this.whispRow = document.createElement('div');
    this.whispRow.setAttribute('role', 'status');
    this.whispRow.setAttribute('data-testid', 'map-whisp-activity');
    this.whispRow.style.padding = '8px';
    this.whispRow.style.fontSize = '12px';
    this.whispRow.style.borderTop = '1px solid #eee';
    this.whispRow.hidden = true;
    root.appendChild(this.whispRow);
    root.appendChild(wrap);

    this._container = root;
    this.list = list;
    return this._container;
  }
  onRemove() {
    this._container?.parentNode?.removeChild(this._container);
    this._map = null;
  }

  setErrors(errs: Issue[]) {
    const ids = new Set(errs.map((e) => e.id));
    if (this.selectedId && !ids.has(this.selectedId)) this.selectedId = null;
    this.errs = errs.slice();
    this.render();
  }

  private render() {
    if (!this._container) return;
    const has = this.errs.length > 0;
    const activityShown = this.tracesState !== 'idle' || this.whispState !== 'idle';
    this._container.style.display = has || this.busy || this.notice || activityShown ? 'flex' : 'none';
    if (!this.busy) {
      this.status.textContent = this.notice;
      this.status.hidden = !this.notice;
    }
    this._container.setAttribute('aria-busy', String(this.busy));
    const repairable = this.errs.some((issue) => issue.kind !== 'kinks');
    this.fixAllButton.disabled = this.busy || !repairable;
    this.fixAllButton.title = has && !repairable ? this.translate('Map_fix_all_manual_only_tr') : '';
    this.cancelButton.hidden = !this.busy;
    this.cancelButton.style.display = this.busy ? 'block' : 'none';
    this.recheckButton.hidden = this.busy || !this.notice;
    this.recheckButton.style.display = !this.busy && this.notice ? 'block' : 'none';

    this.list.replaceChildren();
    for (const e of this.errs) {
      const row = document.createElement('div');
      row.style.display = 'grid';
      row.style.gridTemplateColumns = e.kind === 'overlap' ? '1fr auto' : '1fr';
      row.style.alignItems = 'center';
      row.style.gap = '8px';
      row.style.border = '1px solid #eee';
      row.style.borderRadius = '6px';
      row.style.padding = '6px 8px';
      row.style.background = this.selectedId === e.id ? '#e8f0fe' : '#fff';
      row.style.cursor = 'pointer';
      row.style.transition = 'background 120ms ease';

      row.addEventListener('mouseenter', () => {
        if (this.selectedId !== e.id) row.style.background = '#f7f7f7';
      });
      row.addEventListener('mouseleave', () => {
        row.style.background = this.selectedId === e.id ? '#e8f0fe' : '#fff';
      });

      const label = document.createElement('div');
      label.style.fontSize = '12px';
      label.textContent = e.message;
      row.appendChild(label);

      if (e.kind === 'overlap') {
        const fix = document.createElement('button');
        fix.type = 'button';
        fix.textContent = 'Fix';
        fix.disabled = this.busy;
        fix.style.padding = '4px 8px';
        fix.style.fontSize = '11px';
        fix.style.textTransform = 'uppercase';
        fix.addEventListener('click', (ev) => {
          ev.stopPropagation();
          this.onFixOverlap(e);
        });
        row.appendChild(fix);
      }

      row.addEventListener('click', () => {
        this.selectedId = e.id;
        this.render();
        this.onRowClick(e);
      });

      this.list.appendChild(row);
    }
  }
}
